import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';

const TIPOS: { value: string; label: string }[] = [
  { value: 'laboratorio_dental', label: 'Laboratorio dental' },
  { value: 'proveedor_materiales', label: 'Proveedor de materiales' },
  { value: 'servicio_mantenimiento', label: 'Servicio de mantenimiento' },
  { value: 'farmacia', label: 'Farmacia' },
  { value: 'otro', label: 'Otro' },
];

const empty = {
  nombre: '', tipo: 'otro', direccion: '', telefono: '',
  email: '', website: '', rfc: '', notas: '',
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  proveedor?: any;
};

export function ProveedorFormDialog({ open, onOpenChange, proveedor }: Props) {
  const qc = useQueryClient();
  const isEdit = !!proveedor;
  const [form, setForm] = useState(empty);

  useEffect(() => {
    if (proveedor) {
      setForm({
        nombre: proveedor.nombre ?? '',
        tipo: proveedor.tipo ?? 'otro',
        direccion: proveedor.direccion ?? '',
        telefono: proveedor.telefono ?? '',
        email: proveedor.email ?? '',
        website: proveedor.website ?? '',
        rfc: proveedor.rfc ?? '',
        notas: proveedor.notas ?? '',
      });
    } else {
      setForm(empty);
    }
  }, [proveedor, open]);

  const f = (field: keyof typeof empty) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(p => ({ ...p, [field]: e.target.value }));

  const mut = useMutation({
    mutationFn: (data: any) =>
      isEdit
        ? api.patch(`/api/proveedores/${proveedor.id}`, data).then(r => r.data)
        : api.post('/api/proveedores', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proveedores'] });
      toast.success(isEdit ? 'Proveedor actualizado' : 'Proveedor creado');
      onOpenChange(false);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar')),
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 focus:outline-none">
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="text-lg font-semibold text-text-primary">
              {isEdit ? 'Editar proveedor' : 'Nuevo proveedor'}
            </Dialog.Title>
            <Dialog.Close className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
              <X size={18} />
            </Dialog.Close>
          </div>

          <form
            onSubmit={e => { e.preventDefault(); mut.mutate(form); }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Nombre <span className="text-red-500">*</span></label>
              <input required value={form.nombre} onChange={f('nombre')} placeholder="Ej. Lab Dental Sánchez"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Tipo <span className="text-red-500">*</span></label>
              <select required value={form.tipo} onChange={f('tipo')}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white">
                {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Teléfono</label>
                <input value={form.telefono} onChange={f('telefono')} placeholder="(55) 1234-5678"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">RFC</label>
                <input value={form.rfc} onChange={f('rfc')} placeholder="ABC123456XYZ"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Correo electrónico</label>
              <input type="email" value={form.email} onChange={f('email')} placeholder="contacto@laboratorio.com"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Dirección</label>
              <input value={form.direccion} onChange={f('direccion')} placeholder="Calle, Colonia, Ciudad"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Sitio web</label>
              <input value={form.website} onChange={f('website')} placeholder="https://..."
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Notas</label>
              <textarea rows={3} value={form.notas} onChange={f('notas')} placeholder="Notas adicionales..."
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none" />
            </div>

            <div className="flex gap-3 pt-2">
              <Dialog.Close className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors">
                Cancelar
              </Dialog.Close>
              <button type="submit" disabled={mut.isPending}
                className="flex-1 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60">
                {mut.isPending ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear proveedor'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
